import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PublicHomePage from './pages/PublicHomePage';
import HomePage from './pages/HomePage';
import ExplorePage from './pages/ExplorePage';
import ChatListPage from './pages/ChatListPage';
import ChatRoomPage from './pages/ChatRoomPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from './pages/SettingsPage';
import SearchPage from './pages/SearchPage';
import PostDetailPage from './pages/PostDetailPage';
import LivePage from './pages/LivePage';
import LiveStreamPage from './pages/LiveStreamPage';
import CreatorDashboardPage from './pages/CreatorDashboardPage';
import SecretChatsPage from './pages/SecretChatsPage';
import SecretChatRoomPage from './pages/SecretChatRoomPage';
import MarketplacePage from './pages/MarketplacePage';
import SellProductPage from './pages/SellProductPage';
import ProductDetailPage from './pages/ProductDetailPage';
import WithdrawalPage from './pages/WithdrawalPage';
import AdminWithdrawalsPage from './pages/AdminWithdrawalsPage';
import OrdersPage from './pages/OrdersPage';
import SupportPage from './pages/SupportPage';
import HashtagPage from './pages/HashtagPage';
import VideoCallPage from './pages/VideoCallPage';
import RulesPage from './pages/RulesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PublicProfilePage from './pages/PublicProfilePage';
import PublicPostPage from './pages/PublicPostPage';
import CoinsPage from './pages/CoinsPage';
import CreatorStudioPage from './pages/CreatorStudioPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import LandingPage from './pages/LandingPage';
import CommunitiesPage from './pages/CommunitiesPage';
import CommunityDetailPage from './pages/CommunityDetailPage';
import DominoGamePage from './pages/DominoGamePage';
import AppointmentsPage from './pages/AppointmentsPage';
import ReelsPage from './pages/ReelsPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import DiscoveryPage from './pages/DiscoveryPage';
import ScheduledPostsPage from './pages/ScheduledPostsPage';

// Layout
import AppLayout from './components/layout/AppLayout';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Public Routes - Always accessible */}
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/feed" replace /> : <LoginPage />
      } />
      <Route path="/register" element={
        isAuthenticated ? <Navigate to="/feed" replace /> : <RegisterPage />
      } />
      
      {/* Public Home Page - Show when not logged in */}
      <Route path="/" element={
        isAuthenticated ? <Navigate to="/feed" replace /> : <LandingPage />
      } />
      
      {/* Public Profile - /@username */}
      <Route path="/@:username" element={<PublicProfilePage />} />
      
      {/* Public Post Page */}
      <Route path="/p/:postId" element={<PublicPostPage />} />
      
      {/* Privacy Policy - Public */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      
      {/* Protected Routes */}
      <Route path="/feed" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<HomePage />} />
      </Route>
      
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route path="explore" element={<ExplorePage />} />
        <Route path="chat" element={<ChatListPage />} />
        <Route path="chat/:chatId" element={<ChatRoomPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="profile/:username" element={<ProfilePage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="edit-profile" element={<EditProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="post/:postId" element={<PostDetailPage />} />
        <Route path="live" element={<LivePage />} />
        <Route path="live/:streamId" element={<LiveStreamPage />} />
        <Route path="creator" element={<CreatorDashboardPage />} />
        <Route path="secret-chats" element={<SecretChatsPage />} />
        <Route path="secret-chat/:chatId" element={<SecretChatRoomPage />} />
        <Route path="marketplace" element={<MarketplacePage />} />
        <Route path="marketplace/sell" element={<SellProductPage />} />
        <Route path="marketplace/product/:productId" element={<ProductDetailPage />} />
        <Route path="marketplace/orders" element={<OrdersPage />} />
        <Route path="withdrawals" element={<WithdrawalPage />} />
        <Route path="admin/withdrawals" element={<AdminWithdrawalsPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="explore/hashtag/:hashtag" element={<HashtagPage />} />
        <Route path="call/:callId" element={<VideoCallPage />} />
        <Route path="rules" element={<RulesPage />} />
        <Route path="coins" element={<CoinsPage />} />
        <Route path="coins/success" element={<CoinsPage />} />
        <Route path="coins/cancel" element={<CoinsPage />} />
        <Route path="studio" element={<CreatorStudioPage />} />
        <Route path="payment/success" element={<PaymentSuccessPage />} />
        <Route path="communities" element={<CommunitiesPage />} />
        <Route path="communities/:communityId" element={<CommunityDetailPage />} />
        <Route path="domino" element={<DominoGamePage />} />
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="reels" element={<ReelsPage />} />
        <Route path="analytics" element={<AnalyticsDashboardPage />} />
        <Route path="discover" element={<DiscoveryPage />} />
        <Route path="scheduled-posts" element={<ScheduledPostsPage />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
